import React, { useState } from 'react';

import { Box, Flex, Grid, Radio } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { components } from 'react-select';

import { useUpdateDeviceLeadsMutation } from '@/store/slices/api/patientsApiSlice';
import { useAddDeviceMutation } from '@/store/slices/api/transmissionReport/transmissionReportsApiSlice';
import formatDate from '@/utils/formatDate';

import { VENDOR_MAPPING } from '@/pages/physician/PatientProfilePage/components/PatientTabs/PatientDeviceScheduleTab/types';
import { LabelAndValue } from '@/types/custom';
import { DeviceVendor, Lead, MatchDevice } from '@/types/matchDevice';
import { DeviceLeadLocation } from '@/types/vendorTransmissionReport';
import DatePickerWrapper from '@/ui/DatePickerWrapper';
import Dropdown from '@/ui/Dropdown';
import CustomModal from '@/ui/Modal';
import TextInputWrapper from '@/ui/TextInputWrapper';
import TextWrapper from '@/ui/TextWrapper';

import LeadsEditModal from '../../DeviceLeads/LeadsEditModal/newLeads';
import styles from '../DeviceEditModal/DeviceEditModal.module.scss';

export const DEVICE_TYPE_OPTIONS = ['CRT-D', 'CRT-P', 'ILR', 'PM', 'ICD'].map((value) => ({
  label: value,
  value
}));

export const MANUFACTURER_OPTIONS: LabelAndValue[] = [
  { label: 'Abbott', value: 'abbott' },
  { label: 'Boston Scientific', value: 'boston_scientific' },
  { label: 'Medtronic', value: 'medtronic' },
  { label: 'Biotronik', value: 'biotronik' },
  { label: 'Sorin', value: 'sorin' }
];

const MRI_COMPATIBILITY_OPTIONS: LabelAndValue[] = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' }
];

const DEVICE_TELEMETRY_OPTIONS: LabelAndValue[] = [
  { label: 'Wireless', value: 'Wireless' },
  { label: 'Manual', value: 'Manual' }
];

interface DeviceAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId?: string;
  devices: MatchDevice[];
}

type DeviceInfo = {
  modelName?: string;
  modelNumber?: string;
  serialNumber?: string;
  type?: string;
  implantDate?: string | null;
  vendor?: DeviceVendor;
  status?: string;
  patientId?: string;
  id?: string;
  deviceTelemetry?: string;
  isMriCompatibility?: boolean | string | null;
};

const getErrorMessage = (error: any): string => {
  if (error?.data?.message) {
    return error.data.message.trim();
  }

  if (error?.message) {
    return error.message.trim();
  }

  if (error?.status && error?.data) {
    return error.data?.message?.trim() || 'An error occurred while updating device information.';
  }

  // Fallback for any other error format
  return 'Failed to update device information.';
};

const REQUIRED_FIELDS: (keyof DeviceInfo)[] = ['vendor', 'type', 'serialNumber', 'implantDate'];

const getRequiredFields = (vendor: DeviceVendor | undefined): (keyof DeviceInfo)[] => {
  const baseFields: (keyof DeviceInfo)[] = ['vendor', 'type', 'serialNumber', 'implantDate'];
  if (vendor === 'biotronik') {
    return [...baseFields, 'modelName'];
  } else {
    return [...baseFields, 'modelNumber'];
  }
};

const DeviceAddModal: React.FC<DeviceAddModalProps> = ({ isOpen, onClose, patientId, devices }) => {
  const [formDeviceInfo, setFormDeviceInfo] = useState<Partial<DeviceInfo>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [addDeviceInfo] = useAddDeviceMutation();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isEditLead, setIsEditLead] = useState(false);
  const [leadsData, setLeadsData] = useState<Lead[] | undefined>([]);
  const [leadStatusErrors, setLeadStatusErrors] = useState<Record<string, string>>({});
  const [showModelSelectModal, setShowModelSelectModal] = useState(false);
  const [modelSelectMode, setModelSelectMode] = useState<'import' | 'manual'>('import');
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [isDeviceExists, setIsDeviceExists] = useState<boolean>(false);
  const [isMultiDeviceFound, setIsMultiDeviceFound] = useState<boolean>(false);
  const [updateDeviceLeads] = useUpdateDeviceLeadsMutation();

  // Custom Option component for device dropdown
  const CustomDeviceOption = (props: any) => {
    const device = devices.find((d) => d.id === props.data.value);

    return (
      <components.Option {...props}>
        <div>
          {props.data.label}
          {device?.implantDate && (
            <div>Implanted: {formatDate(device.implantDate, 'MMM YYYY')}</div>
          )}
        </div>
      </components.Option>
    );
  };

  const validate = () => {
    const requiredFields = getRequiredFields(formDeviceInfo.vendor);
    const newErrors: Record<string, string> = {};
    requiredFields.forEach((field) => {
      if (!formDeviceInfo[field] || String(formDeviceInfo[field]).trim() === '') {
        newErrors[field] = 'Required';
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = () => {
    const requiredFields = getRequiredFields(formDeviceInfo.vendor);
    return requiredFields.every(
      (field) => formDeviceInfo[field] && String(formDeviceInfo[field]).trim() !== ''
    );
  };

  const handleFieldChange = (field: keyof DeviceInfo, value: string | DeviceVendor) => {
    setFormDeviceInfo((prev) => ({ ...prev, [field]: value }));

    if (field === 'type') {
      const matchingDevices = devices.filter((d) => d.type === value);
      setIsDeviceExists(matchingDevices.length > 0);
      if (matchingDevices.length === 1) {
        setSelectedModel(matchingDevices[0].id);
        setLeadsData(getLeadsForSelectedDevice(matchingDevices[0].id));
      }
      setIsMultiDeviceFound(matchingDevices.length > 1);
    }

    if (REQUIRED_FIELDS.includes(field)) {
      setErrors((prev) => ({ ...prev, [field]: value ? '' : 'Required' }));
    }
  };

  const handleImplantDateChange = (date: Date | null) => {
    if (date === undefined) return;
    const dateString = date === null ? '' : dayjs(date).format('MM/DD/YYYY');
    handleFieldChange('implantDate', dateString);
  };

  // Check if device type is ILR
  const isILRDevice = formDeviceInfo.type === 'ILR';

  // Button text logic (reacts to type selection)
  const getButtonText = () => {
    if (isEditLead) return 'Save Changes';
    if (formDeviceInfo.type === 'ILR') return 'Save Changes';
    return 'Next';
  };

  // Next/save handler
  const handlePrimaryAction = async () => {
    if (!validate()) return;
    if (!isEditLead && !isILRDevice) {
      // Check for existing device type
      if (!showModelSelectModal) {
        const matchingDevices = devices.filter((d) => d.type === formDeviceInfo.type);
        if (matchingDevices.length > 0) {
          setShowModelSelectModal(true);
          return;
        } else {
          // No device found, go to leads edit with empty form
          setLeadsData(getLeadsForSelectedDevice('EmptyLeads'));
        }
      }
      setShowModelSelectModal(false);
      setIsEditLead(true);
      return;
    }
    let deviceLeadPayload;
    if (!isILRDevice) {
      // Only include leads with at least one of the required fields filled
      const filteredLeads = (leadsData ?? []).filter((lead) => {
        return (
          !!lead.implantDate ||
          (lead.leadCapped !== undefined && lead.leadCapped !== null) ||
          !!lead.leadLocation ||
          !!lead.leadModel ||
          !!lead.serialNumber ||
          !!lead.leadManufacturer?.leadVendorId
        );
      });

      // Check for missing leadStatus in leads with data
      const missingStatusLead = filteredLeads.find((lead) => !lead.leadStatus);
      if (missingStatusLead) {
        notifications.show({
          title: 'Missing Lead Status',
          message: 'Please select a Status for all leads with data.',
          color: 'red'
        });
        return;
      }

      deviceLeadPayload = filteredLeads.map((lead) => ({
        ...lead,
        id: lead.id?.includes('tempid-') ? undefined : lead.id,
        leadManufacturer: lead.leadManufacturer
          ? {
              ...lead.leadManufacturer,
              leadVendorId:
                typeof lead.leadManufacturer === 'object' && 'leadVendorId' in lead.leadManufacturer
                  ? (lead.leadManufacturer as any).leadVendorId
                  : lead.leadManufacturer
            }
          : { leadVendorId: null },
        implantDate: lead.implantDate ? lead.implantDate : null,
        leadLocation: lead.leadLocation ? lead.leadLocation : null
      }));
    }

    // Save device and leads
    setIsSaving(true);
    const payload: DeviceInfo = {
      ...formDeviceInfo,
      status: 'Active',
      implantDate: formDeviceInfo.implantDate
        ? dayjs(formDeviceInfo.implantDate).format('YYYY-MM-DDT00:00:00')
        : null,
      deviceTelemetry:
        formDeviceInfo.deviceTelemetry?.toString().trim() === '-'
          ? undefined
          : formDeviceInfo.deviceTelemetry,
      isMriCompatibility:
        formDeviceInfo.isMriCompatibility === 'true'
          ? true
          : formDeviceInfo.isMriCompatibility === 'false'
            ? false
            : null
    };
    try {
      const data = await addDeviceInfo({
        patientId: patientId ?? '',
        ...payload
      } as any).unwrap();
      notifications.show({
        title: 'Success',
        message: 'Device created successfully.',
        color: 'green'
      });
      // Set deviceId for leads
      setLeadsData((prev) => prev?.map((lead) => ({ ...lead, deviceId: data?.id ?? '' })));
      // Only update leads if it's not an ILR device
      if (!isILRDevice) {
        await updateDeviceLeads({
          patientId: patientId ?? '',
          deviceLeads:
            deviceLeadPayload?.map((lead) => ({
              ...lead,
              deviceId: data?.id ?? ''
            })) ?? [],
          deviceId: data?.id ?? '',
          reportId: undefined
        }).unwrap();
      }
      setFormDeviceInfo({});
      setErrors({});
      setIsEditLead(false);
      setLeadsData([]);
      setLeadStatusErrors({});
      setShowModelSelectModal(false);
      setSelectedModel('');
      setModelSelectMode('import');
      setIsMultiDeviceFound(false);
      setIsDeviceExists(false);
      onClose();
    } catch (error) {
      console.error('Device creation error:', error);
      const errorMessage = getErrorMessage(error);
      notifications.show({
        title: 'Error',
        message: errorMessage,
        color: 'red',
        autoClose: false
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Lead modal handlers
  const handleLeadFieldChange = (leadId: string, field: keyof Lead, value: any) => {
    setLeadsData((prev) =>
      prev?.map((lead) => (lead.id === leadId ? { ...lead, [field]: value } : lead))
    );
  };
  const handleAddLead = (newLead: Lead) => {
    setLeadsData((prev) => [...(prev || []), newLead]);
  };
  const handleDeleteLead = (leadId: string) => {
    setLeadsData((prev) => prev?.filter((lead) => lead.id !== leadId));
  };
  const handleLeadStatusError = (leadId: string, error?: string) => {
    setLeadStatusErrors((prev) => {
      if (error) return { ...prev, [leadId]: error };
      const { [leadId]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleHeadingTextChange = () => {
    if (isMultiDeviceFound && !isEditLead && showModelSelectModal)
      return 'Multiple Device Models Found';
    if (showModelSelectModal && !isEditLead && !isMultiDeviceFound)
      return 'Import Existing Device Data?';
    if (isEditLead) return 'Leads';
    return 'Add Device Info';
  };

  const handleCancel = () => {
    setFormDeviceInfo({});
    setErrors({});
    setIsEditLead(false);
    setLeadsData([]);
    setLeadStatusErrors({});
    setShowModelSelectModal(false);
    setSelectedModel('');
    setModelSelectMode('import');
    setIsMultiDeviceFound(false);
    setIsDeviceExists(false);
    onClose();
  };

  const getLeadsForSelectedDevice = (deviceId: string) => {
    if (!deviceId) return [];
    const chambers: DeviceLeadLocation[] = [
      DeviceLeadLocation.RA,
      DeviceLeadLocation.RV,
      DeviceLeadLocation.LV
    ];
    const importedLeads: Lead[] =
      devices
        .find((d) => d.id === deviceId)
        ?.leads?.filter((lead) => lead.leadStatus === 'Active')
        ?.map((lead) => ({
          ...lead,
          id: `tempid-${Math.random().toString(36).slice(2)}`,
          deviceId: ''
        })) ?? [];
    // Map by chamber
    const leadsByChamber: Record<DeviceLeadLocation, Lead | undefined> = {
      [DeviceLeadLocation.RA]: undefined,
      [DeviceLeadLocation.RV]: undefined,
      [DeviceLeadLocation.LV]: undefined
    };
    importedLeads.forEach((lead) => {
      if (lead.leadChamber) {
        leadsByChamber[lead.leadChamber as DeviceLeadLocation] = lead;
      }
    });
    // Ensure every chamber has a lead
    const leads: Lead[] = chambers.map((chamber) => {
      if (leadsByChamber[chamber]) {
        return leadsByChamber[chamber]!;
      }
      return {
        id: `tempid-${Math.random().toString(36).slice(2)}`,
        leadChamber: chamber,
        leadLocation: undefined,
        implantDate: undefined,
        serialNumber: undefined,
        leadCapped: undefined,
        leadStatus: undefined,
        deviceId: '',
        leadModel: undefined,
        leadManufacturer: { leadVendorId: null }
      };
    });
    return leads;
  };

  const renderSelect = (
    label: string,
    options: LabelAndValue[],
    field: keyof DeviceInfo,
    value: string | DeviceVendor | undefined
  ) => {
    const requiredFields = getRequiredFields(formDeviceInfo.vendor);
    const selected = options.find((opt) => String(opt.value) === String(value));
    return (
      <Dropdown
        label={label}
        options={options}
        value={selected}
        onChange={(option) => handleFieldChange(field, (option as LabelAndValue).value)}
        placeholder={`Select ${label}`}
        labelTextProps={{ variant: 'regular' }}
        required={requiredFields.includes(field)}
      />
    );
  };

  const renderTextInput = (label: string, field: keyof DeviceInfo) => {
    const requiredFields = getRequiredFields(formDeviceInfo.vendor);
    return (
      <TextInputWrapper
        label={label}
        value={
          typeof formDeviceInfo[field] === 'boolean'
            ? String(formDeviceInfo[field])
            : (formDeviceInfo[field] ?? '')
        }
        onChange={(e) => handleFieldChange(field, e.target.value)}
        error={errors[field]}
        required={requiredFields.includes(field)}
      />
    );
  };

  // Helper for device model dropdown with styled options
  const getDeviceModelOptionsAndValue = () => {
    const options = devices
      .filter((d) => d.type === formDeviceInfo.type)
      .map((d) => ({
        label: `${VENDOR_MAPPING[d.vendor]} - ${d.type}`,
        value: d.id
      }));
    const value = options.find((opt) => opt.value === selectedModel);
    return { options, value };
  };

  return (
    <>
      <CustomModal
        modalProps={{
          opened: isOpen,
          onClose: handleCancel,
          size: 'xl',
          closeOnClickOutside: false,
          closeOnEscape: false
        }}
        header={{
          title: handleHeadingTextChange()
        }}
        primaryButton={{
          text: getButtonText(),
          isLoading: isSaving,
          onClick: handlePrimaryAction,
          isDisabled: isSaving || !isFormValid()
        }}
        secondaryButton={{ text: 'Cancel', isDisabled: isSaving, onClick: handleCancel }}>
        {showModelSelectModal ? (
          <Box p={20}>
            <Flex align="center">
              <Box>
                <TextWrapper
                  text={
                    isMultiDeviceFound
                      ? 'Duplicate device detected. Import Leads or enter manually?'
                      : 'Choose a device model to import leads, or enter manually.'
                  }
                  variant="heading"
                />
              </Box>
            </Flex>
            <Box>
              <Radio.Group
                value={modelSelectMode}
                onChange={(val) => {
                  setModelSelectMode(val as 'import' | 'manual');
                  if (val === 'import') {
                    if (getDeviceModelOptionsAndValue().options.length === 1) {
                      setSelectedModel(getDeviceModelOptionsAndValue().options[0].value);
                      setLeadsData(
                        getLeadsForSelectedDevice(getDeviceModelOptionsAndValue().options[0].value)
                      );
                    }
                    setIsDeviceExists(true);
                  } else {
                    setLeadsData(getLeadsForSelectedDevice('EmptyLeads'));
                    setIsDeviceExists(false);
                  }
                }}>
                <Flex gap={25} mt={8}>
                  <Radio value="import" label="Import from Device" />
                  <Radio value="manual" label="Enter Manually" />
                </Flex>
              </Radio.Group>
            </Box>
            {modelSelectMode === 'import' && isDeviceExists && (
              <Box w={'50%'}>
                <TextWrapper text="Device Model" size="sm" variant="normal" mt={20} />
                <Dropdown
                  options={getDeviceModelOptionsAndValue().options}
                  value={getDeviceModelOptionsAndValue().value}
                  onChange={(option) => {
                    setSelectedModel(option?.value ?? '');
                    setLeadsData(getLeadsForSelectedDevice(option?.value ?? ''));
                  }}
                  placeholder="Select a Model"
                  customComponents={{
                    Option: CustomDeviceOption
                  }}
                />
              </Box>
            )}
          </Box>
        ) : isEditLead ? (
          <LeadsEditModal
            leadsData={leadsData}
            onLeadFieldChange={handleLeadFieldChange}
            onAddLead={handleAddLead}
            onDeleteLead={handleDeleteLead}
            leadStatusErrors={leadStatusErrors}
            onLeadStatusError={handleLeadStatusError}
            deviceId={formDeviceInfo.id as string}
          />
        ) : (
          <div className={styles.deviceSection}>
            <Grid mt="md" align="center">
              <Grid.Col span={{ base: 12, sm: 4 }}>
                {renderSelect(
                  'Manufacturer',
                  MANUFACTURER_OPTIONS,
                  'vendor',
                  formDeviceInfo.vendor
                )}
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 4 }}>
                {renderSelect('Type', DEVICE_TYPE_OPTIONS, 'type', formDeviceInfo.type)}
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 4 }}>
                {renderTextInput('Serial Number', 'serialNumber')}
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 4 }}>
                {renderTextInput('Model Name', 'modelName')}
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 4 }}>
                {renderTextInput('Model Number', 'modelNumber')}
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 4 }}>
                <DatePickerWrapper
                  inputProps={{ label: 'Implant Date' }}
                  datePickerProps={{
                    id: 'implantDate',
                    selected:
                      formDeviceInfo.implantDate && formDeviceInfo.implantDate !== '-'
                        ? dayjs(formDeviceInfo.implantDate).toDate()
                        : null,
                    onChange: (date: Date | null) => handleImplantDateChange(date),
                    isClearable: true,
                    required: true
                  }}
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 4 }}>
                {renderSelect(
                  'Device Telemetry',
                  DEVICE_TELEMETRY_OPTIONS,
                  'deviceTelemetry',
                  String(formDeviceInfo.deviceTelemetry)
                )}
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 4 }}>
                {renderSelect(
                  'MRI Compatibility',
                  MRI_COMPATIBILITY_OPTIONS,
                  'isMriCompatibility',
                  String(formDeviceInfo.isMriCompatibility)
                )}
              </Grid.Col>
            </Grid>
          </div>
        )}
      </CustomModal>
    </>
  );
};

export default DeviceAddModal;
